import { useEffect } from "react";

interface ShortcutOptions {
  /** Letter key (single character) or special key like "Enter", "Escape" */
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Treat meta OR ctrl as the same modifier (cross-platform default) */
  mod?: boolean;
  /** Allow the shortcut to fire while inside form fields. Default: false */
  allowInInput?: boolean;
  /** Disable without unmounting the hook */
  enabled?: boolean;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

export function useShortcut(
  opts: ShortcutOptions,
  handler: (e: KeyboardEvent) => void,
) {
  useEffect(() => {
    if (opts.enabled === false) return;
    const onKey = (e: KeyboardEvent) => {
      if (!opts.allowInInput && isEditable(e.target)) return;
      const keyMatch = e.key.toLowerCase() === opts.key.toLowerCase();
      if (!keyMatch) return;
      if (opts.mod) {
        if (!(e.metaKey || e.ctrlKey)) return;
      } else {
        if (opts.meta && !e.metaKey) return;
        if (opts.ctrl && !e.ctrlKey) return;
      }
      if (opts.shift && !e.shiftKey) return;
      if (opts.alt && !e.altKey) return;
      handler(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    opts.key,
    opts.meta,
    opts.ctrl,
    opts.shift,
    opts.alt,
    opts.mod,
    opts.allowInInput,
    opts.enabled,
    handler,
  ]);
}
